import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { Checkbox } from '@components/Checkbox/Checkbox';
import { LibraryCardPlaceholder } from '@components/LibraryThumbnail/LibraryCardPlaceholder';
import { useLibraryCover } from '@components/LibraryThumbnail/useLibraryThumbnail';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { formatLibraryStats, joinLibraryStats } from '@lib/utils/libraryStats';
import { TranscriptOnlyIndicator } from '@pages/Libraries/components/TranscriptOnlyIndicator';
import { LibraryStore } from '@stores/Library/Library';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import css from './LibrarySwitchRow.module.scss';

export const LibrarySwitchRow = observer(
  ({
    library,
    closeSwitch,
    isSelected,
    onSelect,
    disabledReason,
    selectionControl = 'single',
    pickerOpen,
  }: {
    library: LibraryStore & { data: NonNullable<LibraryStore['data']> };
    closeSwitch: () => void;
    isSelected?: boolean;
    /** When provided (destination mode), the row selects via this callback and
     *  never touches the search-selection state. Single-select: clicking always
     *  selects this library and closes. */
    onSelect?: () => void;
    /**
     * Why this library can't be picked. When set the row goes inert and this replaces its
     * stats line — the reason lands in the slot the fact already occupied, so nothing is
     * added and the row reads quieter, not busier.
     *
     * Deliberately a string rather than a boolean: a disabled row with no stated reason
     * just looks broken. The caller owns the wording (see `disabledReasonFor` on
     * `LibrarySwitch`).
     */
    disabledReason?: string;
    /**
     * The selection affordance to render: `none` for pickers where a click acts immediately,
     * `single` for a rounded "pick one" box, `multi` for a square "pick several" one.
     *
     * One enum rather than a hide-flag plus a shape-flag plus an inference: the row used to
     * guess its own shape from `libraryManager`, which its only parent already knew, and the
     * guess and the override could disagree with nothing to catch it.
     */
    selectionControl?: 'none' | 'single' | 'multi';
    /**
     * Whether the picker is currently open. Load-bearing on mobile, not just
     * belt-and-braces: `MobileDrawer` → `SwipeableDrawer` keeps its subtree
     * mounted after the first open, hidden by an off-screen `Slide` transform
     * and `visibility: hidden`. Neither clips, and `IntersectionObserver`
     * ignores visibility — so measured against the scroll container the rows read
     * as on-screen while the drawer is shut, which would fetch every cover for a
     * sheet nobody is looking at.
     */
    pickerOpen: boolean;
  }) => {
    const isMobile = useIsMobile();
    const libraryManager = useLibraryManager();
    const disabled = !!disabledReason;
    const selected =
      isSelected ??
      (onSelect ? false : libraryManager.selectedLibraryIdsToSearch.has(library.libraryId));

    // Until the row nears the scroll window it renders the same placeholder it
    // already shows before any cover resolves, so there is nothing to hide.
    const rowRef = useRef<HTMLDivElement>(null);
    const {
      url: thumbnailUrl,
      token: thumbnailToken,
      onError,
    } = useLibraryCover(library, rowRef, { enabled: pickerOpen });
    const stats = formatLibraryStats(library);

    const onClick = () => {
      if (disabled) {
        // Worth knowing how often users aim at a library the picker has ruled out — a high rate
        // says the reason needs to be visible before the click, not after it.
        trackingStore.libraryPickerRowBlocked({ libraryId: library.libraryId });
        return;
      }

      if (onSelect) {
        onSelect();
        closeSwitch();
        return;
      }

      if (
        !libraryManager.multiLibrarySelection &&
        libraryManager.selectedLibraryIdsToSearch.has(library.libraryId)
      ) {
        return;
      }

      libraryManager.toggleLibraryToSearchSelection(library.libraryId);

      if (!libraryManager.multiLibrarySelection) {
        closeSwitch();
      }
    };

    return (
      <div
        ref={rowRef}
        className={classNames(css.library, {
          [css.selected]: selected && !disabled,
          [css.disabled]: disabled,
        })}
        key={library.libraryId}
        onClick={onClick}
        aria-disabled={disabled || undefined}
      >
        <div className={css.libraryContent}>
          {thumbnailUrl ? (
            <AuthenticatedImage
              className={css.libraryThumbnail}
              src={thumbnailUrl}
              token={thumbnailToken}
              onError={onError}
            />
          ) : (
            <div className={css.libraryThumbnail}>
              <LibraryCardPlaceholder seed={library.libraryId} />
            </div>
          )}
          <div className={css.libraryInfo}>
            {/* Same indicator the library cards (`LibraryCard`) and the table's Name cell
                (`LibraryNameBadges`) use, so "only transcript search works here" is one glyph
                everywhere it's true. It belongs in a picker especially: this is the moment the
                user chooses what to search, and the constraint changes which library they'd pick.
                Ungated, exactly like those two. Its tooltip is effectively desktop-only — MUI
                tooltips don't fire on touch — but that is true of this glyph everywhere in the
                app, so singling this row out would make it the inconsistent one. The stats stay
                visible either way; they are what the user is actually comparing rows on. */}
            <div className={css.libraryNameRow}>
              <span className={css.libraryName}>{library.data.name}</span>
              {library.isTranscriptOnly && (
                <TranscriptOnlyIndicator className={css.transcriptBadge} />
              )}
            </div>
            {/* The stats line doubles as the row's explanation slot when a row can't be picked:
                for an empty library the stats read "0 media • 0 MB • 0s", three meaningless
                zeros, so swapping them for the reason displaces nothing and explains the inert
                state where the user is already looking. Deliberately not a tooltip — MUI
                tooltips need a wrapper on inert targets and never fire on touch, and this
                popover is a `MobileDrawer` on mobile. */}
            <div className={css.libraryBreakdown}>
              {disabledReason ?? joinLibraryStats([stats.count, stats.size, stats.duration], ' • ')}
            </div>
          </div>
        </div>
        {/* No checkbox on an inert row: `Checkbox` has no disabled state, and one that
            can't be checked misrepresents the row. Nothing shifts in either case —
            `.libraryContent` keeps `flex: 1`. */}
        {!isMobile && !disabled && selectionControl !== 'none' && (
          <Checkbox
            checked={selected}
            onChange={() => {}} // handled by row onClick
            theme="monochrome"
            rounded={selectionControl === 'single'}
          />
        )}
      </div>
    );
  },
);
